import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const PAGE_OPTS = [
  { value: '__all__', label: 'Alle Seiten' },
  { value: 'home',    label: 'Startseite' },
  { value: 'detail',  label: 'Rezept-Detail' },
  { value: 'upload',  label: 'Upload' },
  { value: 'rezepte', label: 'Rezeptliste' },
];
const PAGE_MAP = Object.fromEntries(PAGE_OPTS.map(p => [p.value, p.label]));

const TYPE_OPTS = [
  { value: 'banner', label: 'Banner' },
  { value: 'text',   label: 'Text' },
  { value: 'html',   label: 'HTML-Block' },
  { value: 'ad',     label: 'Werbeplatz' },
];

const s = {
  wrap:   { padding: '28px 32px', maxWidth: 1040 },
  h1:     { fontSize: '1.4rem', color: 'var(--admin-text)', fontFamily: 'Inter,sans-serif', fontWeight: 600, marginBottom: 6 },
  sub:    { fontSize: '.78rem', color: 'var(--admin-text-dim)', marginBottom: 28 },
  secLbl: { fontSize: '.7rem', fontWeight: 600, color: 'var(--admin-text-dim)', textTransform: 'uppercase', letterSpacing: '.08em' },
  secRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  btn:    { background: 'var(--admin-btn-primary)', border: 'none', color: '#fff', borderRadius: 5, padding: '7px 16px', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, overflow: 'hidden', marginBottom: 40 },
  th:     { padding: '10px 16px', textAlign: 'left', color: 'var(--admin-text-dim)', fontWeight: 600, background: 'var(--admin-bg-dark)', borderBottom: '1px solid var(--admin-border)' },
  tdFn:   (i) => ({ padding: '10px 16px', borderBottom: '1px solid var(--admin-border)', background: i % 2 === 0 ? 'var(--admin-bg)' : 'var(--admin-bg-alt)', verticalAlign: 'middle' }),
  toggle: (on) => ({ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', background: on ? 'var(--admin-btn-primary)' : 'var(--admin-text-dim)', position: 'relative', transition: 'background .2s', flexShrink: 0 }),
  knob:   (on) => ({ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s' }),
  btnSm:  { background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 4, padding: '3px 10px', fontSize: '.68rem', cursor: 'pointer' },
  btnDel: { background: 'none', border: '1px solid var(--admin-danger)', color: 'var(--admin-danger)', borderRadius: 4, padding: '3px 9px', fontSize: '.68rem', cursor: 'pointer' },
  errBox: { background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 6, padding: '10px 14px', color: 'var(--admin-danger)', fontSize: '.78rem', marginBottom: 20 },
  overlay:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  mbox:   { background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 10, padding: 28, width: 460, maxHeight: '90vh', overflowY: 'auto' },
  lbl:    { display: 'block', fontSize: '.68rem', color: 'var(--admin-text-muted)', marginBottom: 4, marginTop: 12 },
  inp:    { width: '100%', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '7px 10px', fontSize: '.78rem', boxSizing: 'border-box' },
  ta:     { width: '100%', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '7px 10px', fontSize: '.75rem', boxSizing: 'border-box', minHeight: 90, fontFamily: 'monospace', resize: 'vertical' },
  card:   { background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, padding: '14px 18px', marginBottom: 8 },
};

const EMPTY_REGION = { name: '', label: '', page_scope: '__all__', type: 'banner', enabled: true, sort_order: 0, content: '{"text": ""}' };

export default function Regions() {
  const [regions, setRegions]  = useState([]);
  const [loading, setLoading]  = useState(true);
  const [error, setError]      = useState('');
  const [editRegion, setEditR]             = useState(null);
  const [isNewR, setIsNewR]                = useState(false);
  const [formErr, setFormErr]              = useState('');
  const [saving, setSaving]                = useState(false);
  const [confirmDeleteRegion, setConfirmDeleteRegion] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setError('');
    try {
      const rd = await api.getAllRegions();
      setRegions(rd.regions || []);
    } catch (e) {
      setError('Fehler beim Laden: ' + (e.message || 'Unbekannt'));
    } finally {
      setLoading(false);
    }
  }

  // ── Regionen ──────────────────────────────────────────────

  function openNew() {
    setIsNewR(true);
    setEditR({ ...EMPTY_REGION });
    setFormErr('');
  }

  function openEdit(r) {
    setIsNewR(false);
    setEditR({ ...r, content: JSON.stringify(r.content || {}, null, 2) });
    setFormErr('');
  }

  async function handleToggle(r) {
    try {
      await api.saveRegion(r.name, { ...r, enabled: !r.enabled });
      setRegions(prev => prev.map(x => x.name === r.name ? { ...x, enabled: !x.enabled } : x));
    } catch (e) { setError(e.message); }
  }

  async function handleDelete(r) {
    setConfirmDeleteRegion(null);
    try { await api.deleteRegion(r.name); await load(); }
    catch (e) { setError(e.message); }
  }

  async function handleSaveRegion(e) {
    e.preventDefault(); setFormErr('');
    let content;
    try { content = JSON.parse(editRegion.content); }
    catch { setFormErr('Content-JSON ist ungültig.'); return; }
    if (!editRegion.name.trim() || !editRegion.label.trim()) { setFormErr('Name und Label sind Pflicht.'); return; }
    setSaving(true);
    try {
      await api.saveRegion(editRegion.name.trim(), {
        label: editRegion.label.trim(),
        page_scope: editRegion.page_scope,
        type: editRegion.type,
        content,
        enabled: editRegion.enabled,
        sort_order: Number(editRegion.sort_order) || 0,
      });
      setEditR(null); await load();
    } catch (e) { setFormErr(e.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--admin-text-dim)' }}>Lade...</div>;

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Regionen & Modul-Bindungen</h1>
      <p style={s.sub}>
        <b style={{ color: 'var(--admin-text-muted)' }}>Regionen</b> sind Inhalts-Slots auf deinen Seiten (z.B. Banner, Werbeplatz).{' '}
        <b style={{ color: 'var(--admin-text-muted)' }}>Modul-Bindungen</b> legen fest auf welchen Seiten ein Modul erscheint.
      </p>

      {error && (
        <div style={s.errBox}>
          {error}
          <button onClick={load} style={{ marginLeft: 12, background: 'none', border: '1px solid var(--admin-danger)', color: 'var(--admin-danger)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: '.72rem' }}>Neu laden</button>
        </div>
      )}

      {confirmDeleteRegion && (
        <div style={{ ...s.errBox, display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
          <span>Region <strong>„{confirmDeleteRegion.label}"</strong> wirklich löschen?</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...s.btnDel, padding: '4px 12px' }} onClick={() => handleDelete(confirmDeleteRegion)}>Ja, löschen</button>
            <button style={{ ...s.btnSm, padding: '4px 12px' }} onClick={() => setConfirmDeleteRegion(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* ── Regionen-Tabelle ─────────────────────── */}
      <div style={s.secRow}>
        <span style={s.secLbl}>Regionen ({regions.length})</span>
        <button style={s.btn} onClick={openNew}>+ Neue Region</button>
      </div>

      <table style={s.table}>
        <thead>
          <tr>
            {['Label', 'Schlüssel', 'Seite', 'Typ', 'An/Aus', 'Aktionen'].map(h => (
              <th key={h} style={s.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {regions.length === 0 && (
            <tr><td colSpan={6} style={{ ...s.tdFn(0), color: 'var(--admin-text-dim)', textAlign: 'center' }}>Keine Regionen</td></tr>
          )}
          {regions.map((r, i) => (
            <tr key={r.name}>
              <td style={{ ...s.tdFn(i), color: 'var(--admin-text)', fontWeight: 600 }}>{r.label}</td>
              <td style={{ ...s.tdFn(i), fontFamily: 'monospace', color: 'var(--admin-text-muted)', fontSize: '.72rem' }}>{r.name}</td>
              <td style={{ ...s.tdFn(i), color: 'var(--admin-text-muted)' }}>{PAGE_MAP[r.page_scope] || r.page_scope}</td>
              <td style={{ ...s.tdFn(i), color: 'var(--admin-text-muted)' }}>{TYPE_OPTS.find(t => t.value === r.type)?.label || r.type}</td>
              <td style={s.tdFn(i)}>
                <button style={s.toggle(r.enabled)} onClick={() => handleToggle(r)}>
                  <div style={s.knob(r.enabled)} />
                </button>
              </td>
              <td style={s.tdFn(i)}>
                <button style={s.btnSm} onClick={() => openEdit(r)}>✏ Bearbeiten</button>
                {' '}
                <button style={s.btnDel} onClick={() => { setError(''); setConfirmDeleteRegion(r); }}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ fontSize: '.73rem', color: 'var(--admin-text-dim)', marginTop: -8 }}>
        Modul-Sichtbarkeit pro Seite wird direkt beim Bearbeiten einer Seite eingestellt (Admin → Seiten → Inhalt bearbeiten).
      </p>

      {/* ── Region bearbeiten / anlegen ─────────── */}
      {editRegion && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setEditR(null)}>
          <div style={s.mbox}>
            <div style={{ color: 'var(--admin-text)', fontWeight: 600, marginBottom: 16 }}>
              {isNewR ? '+ Neue Region' : `✏ ${editRegion.label || 'Region bearbeiten'}`}
            </div>
            <form onSubmit={handleSaveRegion}>
              <label style={{ ...s.lbl, marginTop: 0 }}>Schlüssel (Name) *</label>
              <input style={{ ...s.inp, fontFamily: 'monospace' }}
                value={editRegion.name}
                placeholder="z.B. top_banner"
                disabled={!isNewR}
                onChange={e => setEditR(p => ({ ...p, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
              />

              <label style={s.lbl}>Label *</label>
              <input style={s.inp} value={editRegion.label} placeholder="z.B. Beta-Banner oben"
                onChange={e => setEditR(p => ({ ...p, label: e.target.value }))} />

              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={s.lbl}>Seite</label>
                  <select style={s.inp} value={editRegion.page_scope}
                    onChange={e => setEditR(p => ({ ...p, page_scope: e.target.value }))}>
                    {PAGE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.lbl}>Typ</label>
                  <select style={s.inp} value={editRegion.type}
                    onChange={e => setEditR(p => ({ ...p, type: e.target.value }))}>
                    {TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div style={{ width: 70 }}>
                  <label style={s.lbl}>Reihenfolge</label>
                  <input style={s.inp} type="number" value={editRegion.sort_order}
                    onChange={e => setEditR(p => ({ ...p, sort_order: e.target.value }))} />
                </div>
              </div>

              <label style={s.lbl}>
                Inhalt (JSON){' '}
                <span style={{ color: 'var(--admin-text-dim)', fontWeight: 400 }}>
                  {editRegion.type === 'banner' && '→ {"text":"...", "link":"", "link_text":""}'}
                  {(editRegion.type === 'html' || editRegion.type === 'ad') && '→ {"html":"..."}'}
                  {editRegion.type === 'text' && '→ {"text":"..."}'}
                </span>
              </label>
              <textarea style={s.ta} value={editRegion.content}
                onChange={e => setEditR(p => ({ ...p, content: e.target.value }))} />

              <label style={{ ...s.lbl, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={editRegion.enabled}
                  onChange={e => setEditR(p => ({ ...p, enabled: e.target.checked }))} />
                Region aktiv (sichtbar)
              </label>

              {formErr && <div style={{ color: 'var(--admin-danger)', fontSize: '.73rem', marginTop: 8 }}>{formErr}</div>}

              <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setEditR(null)}
                  style={{ background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 5, padding: '7px 16px', fontSize: '.75rem', cursor: 'pointer' }}>Abbrechen</button>
                <button type="submit" disabled={saving}
                  style={{ background: 'var(--admin-btn-primary)', border: 'none', color: '#fff', borderRadius: 5, padding: '7px 16px', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' }}>
                  {saving ? 'Speichert…' : '✓ Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
